import { useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import { Pane, Loader, Button, Switch } from 'components/common';
import { useGet, usePost } from 'hooks';
import { listAccountProducts, reorderAccountProducts } from 'services/api';

import Modals from './Modals';
import Table from './Table';
import { ActionsContainer } from './styled';

const Products = () => {
  const [showInactive, setShowInactive] = useState(true);
  const [modal, setModal] = useState('');
  const [activeProduct, setActiveProduct] = useState(null);
  const [reorderInFlight, setReorderInFlight] = useState(false);

  const [{ res, loading }, fetchProducts] = useGet({
    url: listAccountProducts,
    onMount: true,
  });

  const [
    { res: reorderRes, err: reorderErr },
    postReorder,
    setReorderRes,
    setReorderErr,
  ] = usePost({ url: reorderAccountProducts });

  const refresh = useCallback(() => fetchProducts(), [fetchProducts]);

  useEffect(() => {
    if (reorderRes) {
      setReorderInFlight(false);
      setReorderRes(null);
      refresh();
    }
  }, [reorderRes, refresh, setReorderRes]);

  useEffect(() => {
    if (reorderErr) {
      setReorderInFlight(false);
      setReorderErr(null);
      if (reorderErr.status === 409) {
        notification.warning({
          message: 'Catalog changed',
          description: 'The list was updated by someone else. Refreshing.',
        });
        refresh();
      } else {
        notification.error({
          message: 'Could not reorder products',
          description: reorderErr.message ?? 'Unexpected error',
        });
      }
    }
  }, [reorderErr, refresh, setReorderErr]);

  const openModal = (name, product) => {
    setActiveProduct(product);
    setModal(name);
  };

  const reorder = useCallback(
    orderedIds => {
      if (reorderInFlight) return;
      setReorderInFlight(true);
      postReorder({ body: { orderedIds } });
    },
    [postReorder, reorderInFlight]
  );

  if (loading || !res) {
    return (
      <Pane title='Products'>
        <Loader />
      </Pane>
    );
  }

  const products = Array.isArray(res) ? res : [];
  const visibleProducts = showInactive
    ? products
    : products.filter(p => p.isActive);

  const handleMove = (productId, direction) => {
    const orderedIds = products.map(p => p.id);
    const idx = orderedIds.indexOf(productId);
    if (idx < 0) return;
    const swapWith = direction === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= orderedIds.length) return;
    [orderedIds[idx], orderedIds[swapWith]] = [
      orderedIds[swapWith],
      orderedIds[idx],
    ];
    reorder(orderedIds);
  };

  return (
    <Pane title='Products'>
      <ActionsContainer style={{ marginTop: 10, alignItems: 'center' }}>
        <Switch
          size='small'
          checked={showInactive}
          onChange={setShowInactive}
          label='Show inactive products'
          style={{ fontSize: 13, marginRight: 10, minWidth: 200 }}
        />
        <Button
          size='small'
          type='primary'
          onClick={() => openModal('addEdit', null)}
        >
          Add Product
        </Button>
      </ActionsContainer>
      <Table
        data={visibleProducts}
        onEdit={p => openModal('addEdit', p)}
        onToggleActive={p => openModal('toggleActive', p)}
        onMove={handleMove}
        canMoveUp={p =>
          products.findIndex(x => x.id === p.id) > 0 && !reorderInFlight
        }
        canMoveDown={p =>
          products.findIndex(x => x.id === p.id) < products.length - 1 &&
          !reorderInFlight
        }
      />
      {modal && (
        <Modals
          modal={modal}
          activeProduct={activeProduct}
          setModal={setModal}
          refresh={refresh}
        />
      )}
    </Pane>
  );
};

export default Products;
